import { useTranslation } from 'react-i18next';
import { Text } from '@/shared/ui';
import Badge from '@/shared/ui/components/basic/Badge';
import { VistoryCard } from './VistoryCard';
import type { VistoryGroups, StatusCounts } from '@/features/vistories/utils/vistory-grouping';
import type { VistoryStatus } from '../types/vistory.types';

interface Props {
  groups: VistoryGroups;
  counts: StatusCounts;
  agencyId: string;
  onUpdate: () => void;
}

const STATUS_ORDER: VistoryStatus[] = ['draft', 'pending_approval', 'approved', 'rejected'];

const BADGE_STYLE: Record<VistoryStatus, { color: string; bg: string }> = {
  draft: { color: 'var(--status-info)', bg: 'var(--status-info-subtle)' },
  pending_approval: { color: 'var(--status-warning)', bg: 'var(--status-warning-subtle)' },
  approved: { color: 'var(--status-success)', bg: 'var(--status-success-subtle)' },
  rejected: { color: 'var(--status-error)', bg: 'var(--status-error-subtle)' },
};

/**
 * Renders the KPI strip and status-grouped vistory card sections.
 */
export function VistoryStatusGroups({ groups, counts, agencyId, onUpdate }: Props) {
  const { t } = useTranslation('features/vistories');

  return (
    <div className="flex flex-col gap-[var(--space-6)]">
      <div className="flex flex-wrap gap-[var(--space-3)]">
        {STATUS_ORDER.map((status) => {
          const tone = BADGE_STYLE[status];
          return (
            <div key={status} className="flex items-center gap-[var(--space-1)]">
              <Badge color={tone.color} backgroundColor={tone.bg} size="sm">
                {counts[status]}
              </Badge>
              <Text span size="xs" className="text-[var(--text-secondary)]">
                {t(`vistory.status.${status}`)}
              </Text>
            </div>
          );
        })}
      </div>

      {STATUS_ORDER.map((status) => {
        const items = groups[status];
        if (items.length === 0) return null;
        return (
          <div key={status} className="flex flex-col gap-[var(--space-3)]">
            <Text span size="sm" fw={600} className="text-[var(--text-primary)]">
              {t(`vistory.status.${status}`)} ({items.length})
            </Text>
            <div className="grid grid-cols-1 gap-[var(--space-4)] md:grid-cols-2 lg:grid-cols-3">
              {items.map((v) => (
                <VistoryCard key={v.id} vistory={v} agencyId={agencyId} onUpdate={onUpdate} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
